import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarRange, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Preset shift codes with colours
const CODES = [
  { key: "M", label: "Morning", cls: "bg-amber-100 text-amber-900" },
  { key: "E", label: "Evening", cls: "bg-indigo-100 text-indigo-900" },
  { key: "F", label: "Full day", cls: "bg-emerald-100 text-emerald-900" },
  { key: "O", label: "Off", cls: "bg-neutral-200 text-neutral-700" },
  { key: "L", label: "Leave", cls: "bg-rose-100 text-rose-900" },
];
const CODE_MAP = Object.fromEntries(CODES.map((c) => [c.key, c]));

const monthLabel = (y, m) =>
  new Date(y, m - 1, 1).toLocaleDateString([], { month: "long", year: "numeric" });

const isoDate = (y, m, d) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

// Compact "10-19" style time display for the cell
const compactTimes = (s, e) => {
  if (!s && !e) return "";
  const h = (t) => (t || "").split(":")[0].replace(/^0/, "") || "0";
  return `${h(s)}-${h(e)}`;
};

export default function DutyChartPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [days, setDays] = useState(new Date(year, month, 0).getDate());
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit dialog state
  const [editing, setEditing] = useState(null); // { empId, empName, date }
  const [dlgShift, setDlgShift] = useState("");
  const [dlgStart, setDlgStart] = useState("");
  const [dlgEnd, setDlgEnd] = useState("");

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data.filter((e) => e.active)))
      .catch(() => toast.error("Could not load employees"));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/duty-chart", { params: { year, month } });
      setEntries(data.entries || []);
      setDays(data.days);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not load duty chart");
    } finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const lookup = useMemo(() => {
    const m = new Map();
    for (const e of entries) m.set(`${e.employee_id}|${e.date}`, e);
    return m;
  }, [entries]);

  const entryAt = (empId, day) => lookup.get(`${empId}|${isoDate(year, month, day)}`);

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const openCell = (emp, day) => {
    if (!canEdit) return;
    const existing = entryAt(emp.id, day);
    setDlgShift(existing?.shift || "");
    setDlgStart(existing?.start_time || "");
    setDlgEnd(existing?.end_time || "");
    setEditing({ empId: emp.id, empName: emp.name, date: isoDate(year, month, day), day });
  };

  const saveCell = async () => {
    if (!editing) return;
    if ((dlgStart && !dlgEnd) || (!dlgStart && dlgEnd)) {
      toast.error("Enter both start and end time, or leave both blank");
      return;
    }
    setSaving(true);
    // Optimistic patch
    setEntries((prev) => {
      const others = prev.filter((e) => !(e.employee_id === editing.empId && e.date === editing.date));
      if (!dlgShift && !dlgStart && !dlgEnd) return others;
      return [...others, { employee_id: editing.empId, date: editing.date, shift: dlgShift, start_time: dlgStart || null, end_time: dlgEnd || null }];
    });
    try {
      await api.post("/duty-chart", {
        employee_id: editing.empId,
        date: editing.date,
        shift: dlgShift,
        start_time: dlgStart || null,
        end_time: dlgEnd || null,
      });
      setEditing(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
      await load();
    } finally { setSaving(false); }
  };

  const clearCell = async () => {
    if (!editing) return;
    setSaving(true);
    setEntries((prev) => prev.filter((e) => !(e.employee_id === editing.empId && e.date === editing.date)));
    try {
      await api.post("/duty-chart", { employee_id: editing.empId, date: editing.date, shift: "" });
      setEditing(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
      await load();
    } finally { setSaving(false); }
  };

  const clearMonth = async () => {
    if (!canEdit) return;
    if (!window.confirm(`Clear the ENTIRE duty chart for ${monthLabel(year, month)}?`)) return;
    setSaving(true);
    try {
      const bulk = [];
      employees.forEach((emp) => {
        for (let d = 1; d <= days; d++) {
          if (entryAt(emp.id, d)) bulk.push({ employee_id: emp.id, date: isoDate(year, month, d), shift: "" });
        }
      });
      if (bulk.length === 0) { toast.info("Already empty."); return; }
      await api.post("/duty-chart/bulk", { entries: bulk });
      toast.success(`Cleared ${bulk.length} cells`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not clear");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="duty-chart-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Roster</p>
          <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2 flex items-center gap-3">
            Duty Chart <CalendarRange className="w-8 h-8 text-primary" aria-hidden="true" />
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            Tap a cell to set a shift preset <em>or</em> exact hours like <span className="font-medium">10:00 – 19:00</span>.
          </p>
        </div>
        {canEdit && (
          <Button
            variant="ghost"
            onClick={clearMonth}
            disabled={saving || loading}
            data-testid="duty-clear-btn"
            className="text-xs uppercase tracking-widest"
          >
            Clear month
          </Button>
        )}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => changeMonth(-1)} data-testid="duty-prev-month">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="font-serif text-xl sm:text-2xl" data-testid="duty-month-label">{monthLabel(year, month)}</div>
        <Button variant="outline" size="sm" onClick={() => changeMonth(1)} data-testid="duty-next-month">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {CODES.map((c) => (
          <span key={c.key} className={`inline-flex items-center gap-2 px-3 py-1 text-xs ${c.cls}`}>
            <span className="font-bold">{c.key}</span> {c.label}
          </span>
        ))}
        <span className="inline-flex items-center gap-2 px-3 py-1 text-xs bg-primary/10 text-primary">
          <span className="font-bold">10-19</span> Custom hours
        </span>
      </div>

      <div className="mt-6 bg-white border border-border overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : employees.length === 0 ? (
          <div className="p-14 text-center">
            <p className="font-serif text-2xl">No active employees yet.</p>
            <p className="text-muted-foreground mt-2">Add staff first to build the roster.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="border-collapse" data-testid="duty-chart-table">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-secondary/60 backdrop-blur border-b border-r border-border text-xs uppercase tracking-widest text-muted-foreground px-3 py-2 text-left min-w-[160px]">
                    Employee
                  </th>
                  {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
                    const dow = new Date(year, month - 1, d).getDay();
                    const isSun = dow === 0;
                    return (
                      <th key={d} className={`border-b border-border px-1 py-2 text-[10px] font-normal ${isSun ? "text-primary" : "text-muted-foreground"}`}>
                        <div className="text-[9px] uppercase">{["S","M","T","W","T","F","S"][dow]}</div>
                        <div className="font-serif text-sm">{d}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp.id} data-testid={`duty-row-${emp.id}`}>
                    <td className="sticky left-0 z-10 bg-white border-b border-r border-border px-3 py-2 text-sm font-medium min-w-[160px]">
                      {emp.name}
                      <div className="text-[10px] text-muted-foreground">{emp.role}</div>
                    </td>
                    {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
                      const entry = entryAt(emp.id, d);
                      const code = entry?.shift || "";
                      const times = compactTimes(entry?.start_time, entry?.end_time);
                      const meta = CODE_MAP[code];
                      const hasContent = code || times;
                      const displayText = times || code || "·";
                      return (
                        <td key={d} className="border-b border-border p-0">
                          <button
                            type="button"
                            disabled={!canEdit}
                            onClick={() => openCell(emp, d)}
                            data-testid={`duty-cell-${emp.id}-${d}`}
                            className={`w-11 h-10 text-[10px] font-bold flex items-center justify-center transition ${
                              times ? "bg-primary/10 text-primary" : meta ? meta.cls : "text-muted-foreground/40 hover:bg-secondary/60"
                            } ${canEdit ? "cursor-pointer" : "cursor-default"}`}
                            title={times ? `${entry.start_time} – ${entry.end_time}` : meta?.label || "Set shift"}
                          >
                            {hasContent ? displayText : "·"}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!canEdit && (
        <p className="mt-4 text-xs text-muted-foreground">Read-only view. Ask a manager or the owner to update.</p>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent data-testid="duty-edit-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing?.empName}
              <div className="text-sm text-muted-foreground font-sans font-normal mt-1">{editing?.date}</div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Shift preset</Label>
              <Select value={dlgShift || "none"} onValueChange={(v) => setDlgShift(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="duty-dlg-shift" className="mt-1"><SelectValue placeholder="Pick a preset" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none (use custom hours below)</SelectItem>
                  {CODES.map((c) => <SelectItem key={c.key} value={c.key}>{c.key} · {c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="dlg-start" className="text-xs uppercase tracking-widest text-muted-foreground">From</Label>
                <Input
                  id="dlg-start"
                  type="time"
                  value={dlgStart}
                  onChange={(e) => setDlgStart(e.target.value)}
                  data-testid="duty-dlg-start"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="dlg-end" className="text-xs uppercase tracking-widest text-muted-foreground">To</Label>
                <Input
                  id="dlg-end"
                  type="time"
                  value={dlgEnd}
                  onChange={(e) => setDlgEnd(e.target.value)}
                  data-testid="duty-dlg-end"
                  className="mt-1"
                />
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              You can set only a preset (e.g. Morning), only custom hours (e.g. 10:00–19:00), or both.
              Leave everything blank + tap Clear to remove this cell.
            </p>
          </div>

          <DialogFooter className="flex-wrap gap-2">
            <Button type="button" variant="ghost" onClick={clearCell} disabled={saving} data-testid="duty-dlg-clear" className="mr-auto text-destructive">
              <Trash2 className="w-4 h-4 mr-2" /> Clear cell
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="button" onClick={saveCell} disabled={saving} data-testid="duty-dlg-save" className="bg-primary text-primary-foreground hover:bg-primary/90">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
